import { Button, Dropdown, Space } from '@/shared/ui/primitives';
import { LinkOutlined, WhatsAppOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';

type PhoneActionLinksProps = {
  phonePrefix?: string | null;
  phoneNumber?: string | null;
};

export default function PhoneActionLinks({ phonePrefix, phoneNumber }: PhoneActionLinksProps) {
  const { t } = useTranslation();
  const contact = buildContactPhone(phonePrefix, phoneNumber);

  if (!contact) return <>—</>;

  const menu = {
    items: [
      {
        key: 'call',
        icon: <LinkOutlined />,
        label: (
          <a href={contact.telHref} style={actionLinkStyle}>
            {t('common.callPhone')}
          </a>
        ),
      },
      {
        key: 'whatsapp',
        icon: <WhatsAppOutlined />,
        label: (
          <a href={contact.whatsappHref} target="_blank" rel="noopener noreferrer" style={actionLinkStyle}>
            {t('common.openWhatsapp')}
          </a>
        ),
      },
    ],
  };

  return (
    <div data-allstak-mask data-sensitive>
      <Space size={6} style={{ flexWrap: 'wrap' }}>
        <Dropdown menu={menu} trigger={['click']} placement="bottomLeft">
          <Button type="link" title={t('common.phoneActions')} style={{ paddingInline: 0 }}>
            {contact.display}
          </Button>
        </Dropdown>
        <Button size="small" href={contact.telHref} title={t('common.callPhone')}>
          {t('common.call')}
        </Button>
        <Button size="small" href={contact.whatsappHref} target="_blank" title={t('common.openWhatsapp')} icon={<WhatsAppOutlined />}>
          {t('common.whatsapp')}
        </Button>
      </Space>
    </div>
  );
}

function buildContactPhone(phonePrefix?: string | null, phoneNumber?: string | null) {
  const local = normalizePart(phoneNumber);
  if (!local) return null;

  const prefix = normalizePrefix(phonePrefix);
  const localForInternational = prefix ? local.replace(/^0+/, '') : local;
  const display = prefix ? `${prefix}${localForInternational}` : local;
  const telNumber = display.replace(/[^\d+]/g, '');
  const whatsappNumber = display.replace(/\D/g, '');

  if (!telNumber || !whatsappNumber) return null;

  return {
    display,
    telHref: `tel:${telNumber}`,
    whatsappHref: `https://wa.me/${whatsappNumber}`,
  };
}

function normalizePart(value?: string | null) {
  return String(value ?? '').trim().replace(/\s+/g, '');
}

function normalizePrefix(value?: string | null) {
  const prefix = normalizePart(value);
  if (!prefix) return '';
  return prefix.startsWith('+') ? prefix : `+${prefix.replace(/^00/, '')}`;
}

const actionLinkStyle = {
  color: 'inherit',
  display: 'block',
  minWidth: 160,
  textDecoration: 'none',
} as const;
